import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Grid,
  Tabs,
  Tab,
  MenuItem,
  Select,
  IconButton,
  Icon,
  Checkbox,
} from '@mui/material';

import { Controller, useForm } from 'react-hook-form';
import GridFormLabel from './GridFormLabel';
import GridFormItem from './GridFormItem';
import GridForm from './GridForm';
import { getErrorProps, getItemErrorProps, getFormModAndID, getFieldProps } from './gridFormUtils';
import GridFormDouble from './GridFormDouble';
import CustomDatePicker from 'app/components/CustomDatePicker/CustomDatePicker';
import WithControllerImageUpload from '../../../components/ImageUpload/WithControllerImageUpload';
import WithControllerAutoComplete from '../../../components/SmartAutoComplete/WithControllerAutoComplete';
import { isArray } from 'lodash';
import useAuth from 'app/hooks/useAuth';
import { translate } from 'translate';
import { RtlTextField } from 'app/components/RTLTextField';
import WithControllerLocation from 'app/components/Map/WithControllerLocation';
import { getDescendantProp } from 'app/views/smart-kit/Form/gridFormUtils';

const SmartGridForm = ({ path, name, fields, entityName, onSubmit, entity, title, onChange }) => {
  const { mod, uuid } = getFormModAndID();
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();

  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
    getValues,
    formState,
  } = useForm({
    defaultValues: useMemo(() => entity, [entity]),
  });

  useEffect(() => {
    setField(fields);
  }, [fields]);

  // console.log(fields)
  const [field, setField] = useState(fields);

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };

  let watches = {};

  for (const field of fields) {
    if (field.show) {
      const w = watch(field.show.key);
      watches[field.show.key] = w;
    }
    if (field.watch) {
      watches[field.key] = watch(field.key);
    }
  }

  useEffect(() => {
    if (onChange) {
      console.log('change', watches);
      onChange(watches);
    }
  }, [watches]);

  const renderFormItem = (f) => {
    if (f)
      return (
        <GridFormItem
          {...getItemErrorProps(errors, f.key, f.type)}
          half={Boolean(f.group)}
          full={f.type === 'multiline'}
        >
          {f.prefix && <span className="absolute left-2 top-2 ltr">{f.prefix}</span>}
          {f.type === 'radio' ? (
            <Controller
              control={control}
              name={f.key}
              defaultValue={''}
              rules={f.validation}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { invalid, isTouched, isDirty, error },
                formState,
              }) => (
                <RadioGroup row name={f.key} value={value} onChange={onChange}>
                  {f.items.map((item, i) => (
                    <FormControlLabel
                      key={item.value}
                      value={item.value}
                      control={<Radio />}
                      label={translate(item.label)}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          ) : f.type === 'image' ? (
            <WithControllerImageUpload
              id={f.key}
              control={control}
              name={f.key}
              validation={f.validation}
              fileInfo={{
                purpose: f.purpose,
                file_type: 'image',
                entity_name: entityName,
                entity_uuid: uuid,
              }}
            />
          ) : f.type === 'location' ? (
            <WithControllerLocation id={f.key} control={control} name={f.key} />
          ) : f.type === 'date' ? (
            <Controller
              control={control}
              name={f.key}
              defaultValue={entity[f.key]}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { invalid, isTouched, isDirty, error },
                formState,
              }) => <CustomDatePicker value={value} onBlur={onBlur} onChange={onChange} />}
            />
          ) : f.type === 'auto_complete' ? (
            <React.Fragment>
              <WithControllerAutoComplete
                control={control}
                name={f.key}
                label={translate(f.label)}
                path={f.options.path}
                render={f.options.render}
                validation={f.validation}
                defaultFilters={f.defaultFilters}
                defaultValue={f.defaultValue}
                disabled={f.disabled}
              />
            </React.Fragment>
          ) : f.type === 'select' || f.type === 'selectMultiple' ? (
            <Controller
              control={control}
              name={f.key}
              defaultValue={f.type === 'select' ? '' : []}
              rules={f.validation}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { invalid, isTouched, isDirty, error },
                formState,
              }) => {
                // console.log(value)
                return (
                  <RtlTextField
                    {...getErrorProps(errors, f.key)}
                    {...getFieldProps(translate(f.label))}
                    value={!value ? (f.type === 'selectMultiple' ? [] : value) : value}
                    onChange={onChange}
                    dir="rtl"
                    select
                    // classes="w-full"
                    SelectProps={{
                      multiple: f.type === 'selectMultiple',
                    }}
                  >
                    {f.items &&
                      f.items.map((item, i) => (
                        <MenuItem key={i} value={item.value}>
                          {translate(item.label)}
                        </MenuItem>
                      ))}
                  </RtlTextField>
                );
              }}
            />
          ) : f.type === 'checkbox' ? (
            <Checkbox
              {...getErrorProps(errors, f.key)}
              {...getFieldProps(translate(f.label))}
              {...register(f.key, f.validation)}
              inputProps={f.inputProps}
              disabled={f.disabled === 'onEdit' ? (mod === 'edit' ? true : false) : f.disabled}
              type={f.type}
              defaultChecked={Boolean(entity[f.key])}
              // defaultValue={f.defaultValue}
              // required
            />
          ) : (
            <RtlTextField
              {...getErrorProps(errors, f.key)}
              {...getFieldProps(translate(f.label))}
              {...register(f.key, f.validation)}
              multiline={f.type === 'multiline'}
              rows={12}
              dir="rtl"
              placeholder={f.placeholder || translate(f.label)}
              className={f.type === 'multiline' ? 'w-full' : ''}
              inputProps={f.inputProps}
              disabled={f.disabled === 'onEdit' ? (mod === 'edit' ? true : false) : f.disabled}
              type={f.type}
              defaultValue={f.defaultValue}
              // required
            >
              {f.items &&
                f.items.map((item, i) => (
                  <MenuItem key={i} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
            </RtlTextField>
          )}
        </GridFormItem>
      );
  };

  const handleDelete = (key) => {
    // console.log(fields.map(f => f.key), key)
    setField(field.filter((f) => f.key !== key));
  };
  return (
    <>
      {entity && (
        <GridForm
          name={name}
          onSubmit={handleSubmit(onSubmit)}
          loading={submitLoading}
          title={translate(title)}
        >
          {field.map((f, index, array) => {
            if (f.show) {
              if (watches[f.show.key]) {
                console.log(
                  'here',
                  f.show.value,
                  watches[f.show.key],
                  watches[f.show.key].includes(f.show.value),
                );
              }
              if (f.show.value === 'any') {
                if (!watches[f.show.key]) {
                  return <React.Fragment key={index} />;
                }
              } else if (
                isArray(watches[f.show.key]) &&
                watches[f.show.key].includes(f.show.value)
              ) {
                // console.log('why in no')
                // return <React.Fragment key={index} />
              } else if (f.show.value !== watches[f.show.key]) {
                return <React.Fragment key={index} />;
              }
            }
            if (
              !user.roles.includes('sa') &&
              !user.roles.includes('admin') &&
              f.auth &&
              f.auth.length &&
              user &&
              user.roles
            ) {
              if (!user.roles.some((e) => f.auth.includes(e))) {
                return <React.Fragment key={index} />;
              }
            }
            if (f.isSection) {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                    <h5>{translate(f.label)}</h5>
                    {translate(f.subtitle) && <h7>{translate(f.subtitle)}</h7>}
                    <Divider />
                  </Grid>
                </React.Fragment>
              );
            }

            if (f.isTab) {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      {f.tabs.map((tab, i) => (
                        <Tab key={i} label={translate(tab)} />
                      ))}
                    </Tabs>
                  </Grid>
                </React.Fragment>
              );
            }
            return (
              (f.tab == tabValue || typeof f.tab === 'undefined') && (
                <React.Fragment key={index}>
                  {!f.groupIndex && (
                    <GridFormLabel
                      required={f.validation && f.validation.required}
                      error={getDescendantProp(errors, f.key)}
                    >
                      {f.group && !f.showLabel
                        ? translate(f.group)
                        : typeof f.label === 'function'
                        ? f.label(entity)
                        : translate(f.label)}
                      {f.deletable ? (
                        <IconButton className="position-bottom" onClick={() => handleDelete(f.key)}>
                          {' '}
                          <Icon fontSize="small">close</Icon>{' '}
                        </IconButton>
                      ) : null}
                    </GridFormLabel>
                  )}

                  {f.groupIndex > 0 ? (
                    <> </>
                  ) : f.group ? (
                    <GridFormDouble>
                      {renderFormItem(f)}
                      {renderFormItem(array[index + 1])}
                    </GridFormDouble>
                  ) : (
                    renderFormItem(f)
                  )}
                </React.Fragment>
              )
            );
          })}
        </GridForm>
      )}
    </>
  );
};

export default SmartGridForm;
